import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, RefreshCw, XCircle, Gem, Home, HelpCircle, Users, ExternalLink, Edit3, TrendingUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import useUpgraderStore from '../store/useUpgraderStore'
import useAppStore from '../store/useAppStore'
import UpgradeWheel from '../components/UpgradeWheel'
import UpgraderSkinCard from '../components/UpgraderSkinCard'
import HelpModal from '../components/HelpModal'
import SeedEditor, { resetSavedSeed } from '../components/SeedEditor'
import AnimatedButton from '../components/AnimatedButton'
import confetti from 'canvas-confetti'
import useAudioManager from '../hooks/useAudioManager'
import useAppEnvironment from '../hooks/useAppEnvironment'
import { hapticImpact, hapticNotify, hapticSelectionChanged } from '../utils/haptic'

const Upgrader = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { loadProfile } = useAppStore()
  const { isWebBrowser } = useAppEnvironment()
  
  const { 
    availableItems, 
    multipliers, 
    isLoading, 
    loadAvailableItems, 
    loadMultipliers,
    calculateChance,
    performUpgrade 
  } = useUpgraderStore()

  const [currentStep, setCurrentStep] = useState(1)
  const [sourceItem, setSourceItem] = useState(null)
  const [targetItem, setTargetItem] = useState(null)
  const [selectedMultiplier, setSelectedMultiplier] = useState(null)
  const [upgradeChance, setUpgradeChance] = useState(0)
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [upgradeResult, setUpgradeResult] = useState(null)
  const [isFirstSuccess, setIsFirstSuccess] = useState(false)
  const [showAllMultipliers, setShowAllMultipliers] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [isSeedEditorOpen, setIsSeedEditorOpen] = useState(false)
  
  // Инициализация Audio Manager через глобальный audioService
  const { playSound } = useAudioManager({
    upgraderWin: 'winV1',
    upgraderFail: 'upgraderFail',
    select: 'select',
    pick: 'pick'
  })

  // Звуки теперь управляются через useAudioManager (Web Audio API)
  // Предзагрузка происходит автоматически в хуке

  useEffect(() => {
    loadAvailableItems()
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    
    // Скрываем UI на шагах 4 и 5
    if (currentStep === 4 || currentStep === 5) {
      if (searchParams.get('hideUI') !== 'true') {
        searchParams.set('hideUI', 'true')
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })
      }
    } else {
      if (searchParams.has('hideUI')) {
        searchParams.delete('hideUI')
        const newSearch = searchParams.toString()
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true })
      }
    }
  }, [currentStep, location.pathname, navigate])

  useEffect(() => {
    if (currentStep === 1 && sourceItem) {
      setShowAllMultipliers(false)
      
      loadMultipliers(sourceItem.id)
        .then(({ multipliers }) => {
          if (multipliers && Object.keys(multipliers).length > 0) {
            const firstMultiplier = Object.keys(multipliers).sort((a, b) => parseFloat(a) - parseFloat(b))[0]
            setSelectedMultiplier(firstMultiplier)
          }
        })
      setCurrentStep(2)
    }
  }, [sourceItem, currentStep])

  useEffect(() => {
    if (currentStep === 2 && targetItem && sourceItem) {
      calculateChance(sourceItem.id, targetItem.id)
        .then(({ chance }) => {
          setUpgradeChance(chance)
          setCurrentStep(3)
        })
        .catch(error => {
          console.error('Ошибка загрузки шанса:', error)
        })
    }
  }, [targetItem, currentStep])

  const getDisplayedMultipliers = () => {
    const allMults = Object.keys(multipliers).sort((a, b) => parseFloat(a) - parseFloat(b))
    
    if (allMults.length <= 5) {
      return allMults
    }
    
    if (showAllMultipliers) {
      return allMults
    }
    
    const result = []
    result.push(allMults[0], allMults[1])
    
    const middleIndex = Math.floor(allMults.length / 2)
    result.push(allMults[middleIndex])
    
    result.push(allMults[allMults.length - 2], allMults[allMults.length - 1])
    
    return result
  }

  // Все звуки теперь воспроизводятся через playSound() из useAudioManager
  // Это обеспечивает надежное воспроизведение через Web Audio API

  const handlePrevStep = () => {
    if (currentStep === 2) {
      setSourceItem(null)
      setTargetItem(null)
      setCurrentStep(1)
    } else if (currentStep === 3) {
      setTargetItem(null)
      setCurrentStep(2)
    }
  }

  const handleUpgrade = async () => {
    if (!sourceItem || !targetItem) return

    // Уверенный импульс на подтверждение старта апгрейда
    hapticImpact('medium')

    // Сбрасываем сохраненный токен перед апгрейдом
    resetSavedSeed()

    // Отправляем цель в Яндекс.Метрику
    if (window.ym) {
      window.ym(104258304, 'reachGoal', 'start-upgrader')
    }

    try {
      const result = await performUpgrade(sourceItem.id, targetItem.id)
      console.log('🎰 Результат апгрейда:', result)
      
      const { win, firstSuccessUpgrade } = result

      // Дополнительная защита: не запускаем колесо при некорректном ответе
      if (typeof win !== 'boolean') {
        throw new Error('Некорректный ответ сервера')
      }

      console.log('🔍 Extracted values:', { win, firstSuccessUpgrade })
      
      setUpgradeResult(win)
      setIsFirstSuccess(firstSuccessUpgrade || false)
      console.log('✅ State updated:', { upgradeResult: win, isFirstSuccess: firstSuccessUpgrade || false })
      
      setCurrentStep(4)
      setIsUpgrading(true)
      
      setTimeout(() => {
        setTimeout(() => {
          setCurrentStep(5)

          if (win) {
            // Воспроизводим звук успешного апгрейда
            playSound('upgraderWin')
            // Success-нотификация на итоговом экране
            hapticNotify('success')
            
            confetti({
              particleCount: 100,
              spread: 70,
              origin: { y: 0.6 },
              colors: ['#00FF7F', '#4EFFC6', '#FFD700']
            })
          } else {
            // Воспроизводим звук неудачного апгрейда
            playSound('upgraderFail')
            hapticNotify('error')
          }
        }, 1000)
      }, 10400)
    } catch (error) {
      console.error('❌ Ошибка выполнения апгрейда:', error)
      hapticNotify('error')
      setCurrentStep(3)
      setIsUpgrading(false)
    }
  }

  const handleReset = async () => {
    // Сначала переходим на шаг 1 чтобы показать анимацию загрузки
    setCurrentStep(1)
    setIsUpgrading(false)
    setUpgradeResult(null)
    setIsFirstSuccess(false)
    setSourceItem(null)
    setTargetItem(null)
    
    try {
      // Загружаем данные (isLoading будет true во время загрузки)
      await loadAvailableItems()
      await loadProfile()
    } catch (error) {
      console.error('❌ Ошибка обновления данных:', error)
    }
  }

  return (
    <div className="min-h-screen bg-dark-primary text-white">
      {/* Sticky Header - скрываем на шагах 4 и 5 */}
      {currentStep !== 4 && currentStep !== 5 && (
      <div className="top-0 z-10 bg-dark-primary/95 backdrop-blur-sm border-b border-white/10 px-6 py-3 lg:border-none">
        <div className="flex items-center gap-4 justify-center">
          {currentStep > 1 && (
            <button 
              onClick={() => {
                if (currentStep > 1 && currentStep < 4) {
                  handlePrevStep()
                } else {
                  navigate('/games')
                }
              }}
              className="p-2 rounded-lg bg-surface hover:bg-opacity-80 transition-all"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div className="flex items-center gap-2">
            <h1 className="flex items-center gap-2 text-2xl font-bold text-accent-primary lg:text-4xl">
              <TrendingUp className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
              <span>{t('upgrader.title')}</span>
            </h1>
            <button
              onClick={() => setShowHelp(true)}
              className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
            >
              <HelpCircle className="w-5 h-5 text-text-secondary" />
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Content */}
      <div className="px-6">
        
        {/* ШАГ 1: Выбор исходного скина */}
        {currentStep === 1 && (
          <div className="space-y-6 pt-6">
            <h2 className="text-2xl font-bold text-white text-center">
              {t('upgrader.step1Title')}
            </h2>
            
            {/* Состояние загрузки */}
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <RefreshCw className="animate-spin text-accent-primary" size={32} />
              </div>
            )}

            {/* Список скинов */}
            {!isLoading && (availableItems.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-3">
                {availableItems.map((skin, index) => (
                  <UpgraderSkinCard
                    key={skin.id}
                    skin={skin}
                    isSelected={sourceItem?.id === skin.id}
                    onClick={() => {
                      playSound('select')
                      hapticSelectionChanged()
                      setSourceItem(skin)
                    }}
                    isNew={index === 0}
                  />
                ))}
              </div>
            ) : (
              // Пустое состояние - нет доступных скинов
              <div className="text-center py-12">
                <div className="mb-6 flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Gem className="w-10 h-10 text-primary" />
                  </div>
                </div>
                <p className="text-text-secondary mb-4 text-lg">
                  {t('upgrader.noItems')}
                </p>
                <p className="text-text-secondary mb-6">
                  {t('upgrader.noItemsDescription')}
                </p>
                <button
                  onClick={() => navigate('/cases')}
                  className="px-6 py-3 bg-accent-primary text-dark-primary font-bold rounded-lg hover:bg-accent-primary/90 transition-colors shadow-lg shadow-accent-primary/30"
                >
                  {t('upgrader.openCases')}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ШАГ 2: Выбор множителя и целевого скина */}
        {currentStep === 2 && (
          <div className="space-y-6 pt-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white text-center">
                {t('upgrader.step2SelectTarget')}
              </h2>

              {/* Состояние загрузки мультипликаторов */}
              {isLoading && (
                <div className="flex justify-center items-center py-12">
                  <RefreshCw className="animate-spin text-accent-primary" size={32} />
                </div>
              )}

              {/* Сообщение если множители не найдены */}
              {!isLoading && Object.keys(multipliers).length === 0 && (
                <div className="text-center py-12">
                  <div className="mb-4">
                    <XCircle size={64} className="text-red-400 mx-auto" />
                  </div>
                  <p className="text-text-secondary text-lg mb-4">
                    {t('upgrader.noMultipliers')}
                  </p>
                  <button
                    onClick={() => {
                      setSourceItem(null)
                      setCurrentStep(1)
                    }}
                    className="px-6 py-3 bg-surface border border-white/20 text-white font-semibold rounded-xl hover:bg-white/5 transition-all"
                  >
                    {t('upgrader.selectAnotherItem')}
                  </button>
                </div>
              )}

              {/* Множители из API */}
              {!isLoading && Object.keys(multipliers).length > 0 && (
                <>
                  <div className="flex gap-2 justify-center flex-wrap">
                    {getDisplayedMultipliers().map((mult) => (
                      <button
                        key={mult}
                        onClick={() => {
                          playSound('pick')
                          hapticSelectionChanged()
                          setSelectedMultiplier(mult)
                          setTargetItem(null)
                        }}
                        className={`px-3 py-2 rounded-lg font-bold transition-all ${
                          selectedMultiplier === mult
                            ? 'bg-accent-primary text-dark-primary scale-110'
                            : 'bg-surface text-white hover:bg-surface/80'
                        }`}
                      >
                        {t('upgrader.multiplier', { value: mult })}
                      </button>
                    ))}
                    
                    {/* Кнопка "Ещё" если множителей больше 5 */}
                    {Object.keys(multipliers).length > 5 && !showAllMultipliers && (
                      <button
                        onClick={() => setShowAllMultipliers(true)}
                        className="px-3 py-2 rounded-lg font-bold bg-surface/50 text-accent-primary hover:bg-surface/80 transition-all border border-accent-primary/30"
                      >
                        {t('upgrader.showAll')}
                      </button>
                    )}
                  </div>
                  
                  {/* Кнопка "Скрыть" если показаны все */}
                  {showAllMultipliers && Object.keys(multipliers).length > 5 && (
                    <button
                      onClick={() => setShowAllMultipliers(false)}
                      className="text-accent-primary text-sm hover:underline"
                    >
                      ▲ {t('upgrader.hideExtra')}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Целевые скины для выбранного мультипликатора */}
            {!isLoading && selectedMultiplier && multipliers[selectedMultiplier] && (
              <>
                {multipliers[selectedMultiplier].length > 0 ? (
                  <div className="flex flex-wrap justify-center gap-3">
                    {multipliers[selectedMultiplier].map((skin) => (
                      <UpgraderSkinCard
                        key={skin.id}
                        skin={skin}
                        isSelected={targetItem?.id === skin.id}
                        onClick={() => {
                          playSound('select')
                          hapticSelectionChanged()
                          setTargetItem(skin)
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-text-secondary py-8">
                    {t('upgrader.noMultipliers')}
                  </p>
                )}
              </>
            )}

            {/* Подсказка выбрать мультипликатор */}
            {!isLoading && !selectedMultiplier && Object.keys(multipliers).length > 0 && (
              <p className="text-center text-text-secondary py-8">
                {t('upgrader.step2Title')}
              </p>
            )}
          </div>
        )}

        {/* ШАГ 3: Подтверждение */}
        {currentStep === 3 && (
          <div className="min-h-[calc(100vh-140px)] flex items-center justify-center px-4 pt-6 lg:max-w-2xl lg:mx-auto lg:min-h-0">
            <div className="w-full space-y-6">
            {/* Превью выбранных скинов */}
            <div className="flex items-center justify-center gap-4">
              <div className="flex-1 bg-surface rounded-lg p-4 border border-white/10">
                <p className="text-xs text-text-secondary mb-2 text-center">{t('upgrader.sourceItem')}</p>
                <div className="h-24 sm:h-28 rounded overflow-hidden mb-2 flex items-center justify-center">
                  <img 
                    src={sourceItem.img} 
                    alt={sourceItem.name}
                    className="w-auto h-auto max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex items-center justify-center gap-1">
                  <Gem size={16} className="text-accent-primary" />
                  <p className="text-accent-primary font-bold text-base">
                    {sourceItem.price.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="text-accent-primary text-3xl">
                →
              </div>

              <div className="flex-1 bg-surface rounded-lg p-4 border border-accent-primary/30">
                <p className="text-xs text-text-secondary mb-2 text-center">{t('upgrader.targetItem')}</p>
                <div className="h-24 sm:h-28 rounded overflow-hidden mb-2 flex items-center justify-center">
                  <img 
                    src={targetItem.img} 
                    alt={targetItem.name}
                    className="w-auto h-auto max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex items-center justify-center gap-1">
                  <Gem size={16} className="text-accent-primary" />
                  <p className="text-accent-primary font-bold text-base">
                    {targetItem.price.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Вероятность с визуальной индикацией */}
            <div className="bg-surface rounded-lg p-6 border border-accent-primary/30">
              <p className="text-text-secondary text-center mb-3">{t('upgrader.upgradeChance')}</p>
              
              {/* Процент */}
              <p className="text-5xl font-bold text-center mb-4" style={{
                color: upgradeChance >= 50 ? '#00FF7F' : upgradeChance >= 15 ? '#FFD700' : '#FF6B6B'
              }}>
                {Math.round(upgradeChance)}%
              </p>

              {/* Прогресс-бар */}
              <div className="relative w-full h-3 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${upgradeChance}%`,
                    background: upgradeChance >= 50 
                      ? 'linear-gradient(90deg, #00FF7F, #4EFFC6)' 
                      : upgradeChance >= 15
                      ? 'linear-gradient(90deg, #FFD700, #FFA500)'
                      : 'linear-gradient(90deg, #FF6B6B, #FF4444)'
                  }}
                />
              </div>
            </div>

            {/* Кнопка апгрейда */}
            <AnimatedButton onClick={handleUpgrade}>
              {t('upgrader.upgrade')}
            </AnimatedButton>

            {/* Ссылка редактирования токена открытия */}
            <div className="text-center mt-4">
              <button
                onClick={() => setIsSeedEditorOpen(true)}
                className="text-text-secondary hover:text-accent-primary transition-colors inline-flex items-center gap-2 text-sm"
              >
                <Edit3 size={16} />
                {t('seedEditor.editSeed')}
              </button>
            </div>
            </div>
          </div>
        )}

        {/* ШАГ 4: Анимация апгрейда */}
        {currentStep === 4 && (
          <div 
            className="fixed inset-0 bg-dark-primary/95 bg-center bg-no-repeat backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto lg:bg-cover"
            style={{
              backgroundImage: 'url(/images/roulette-background.png)'
            }}
          >
            <UpgradeWheel 
              targetItem={targetItem}
              chance={upgradeChance}
              isSpinning={isUpgrading}
              result={upgradeResult}
            />
          </div>
        )}

        {/* ШАГ 5: Результат апгрейда */}
        {currentStep === 5 && upgradeResult !== null && (
          <div 
            className="fixed inset-0 bg-dark-primary/95 bg-center bg-no-repeat backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto lg:bg-cover"
            style={{
              backgroundImage: 'url(/images/roulette-background.png)'
            }}
          >
            <div className=" rounded-2xl max-w-md w-full relative overflow-hidden my-auto p-8">
              {/* Фоновый эффект */}
              <div className={`absolute inset-0 opacity-10 ${upgradeResult ? '' : 'bg-gray-500'}`}></div>
              
              <div className="relative z-10">

                {/* Заголовок */}
                <h2 className={`text-3xl font-bold text-center mb-6 ${upgradeResult ? 'text-accent-primary' : 'text-white'}`}>
                  {upgradeResult ? t('upgrader.step5Success') : t('upgrader.step5Fail')}
                </h2>

                {/* Изображение результата */}
                {upgradeResult ? (
                  <div className="relative mb-8 max-w-sm w-full mx-auto">
                    {/* Glow Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-accent-primary/20 via-accent-secondary/30 to-accent-primary/20 rounded-lg blur-xl animate-pulse"></div>
                    
                    {/* Main Card */}
                    <div className="relative bg-surface rounded-lg p-6 border-2 border-accent-primary/50 shadow-[0_0_30px_rgba(0,255,127,0.3)] animate-bounce-slow">
                      <div className="text-center">
                        {/* Item Image with Glow */}
                        <div className="relative w-32 h-32 mx-auto mb-4">
                          <div className="absolute inset-0 bg-accent-primary/20 rounded-lg blur-md animate-pulse"></div>
                          <div className="relative w-full h-full bg-dark-primary rounded-lg border border-accent-primary/50 overflow-hidden">
                            <img 
                              src={targetItem.img} 
                              alt={targetItem.name}
                              className="w-full h-full object-contain p-2 animate-pulse"
                            />
                          </div>
                        </div>
                        
                        {/* Item Name with Glow */}
                        <h3 className="text-xl font-bold text-center mb-2 drop-shadow-[0_0_10px_rgba(0,255,127,0.5)]">
                          {targetItem.name?.includes('|') ? (
                            <>
                              <div className="text-white">
                                {targetItem.name.split('|')[0].trim()}
                              </div>
                              <div className="text-text-secondary text-base mt-1 font-normal">
                                {targetItem.name.split('|')[1].trim()}
                              </div>
                            </>
                          ) : (
                            <div className="text-white">
                              {targetItem.name}
                            </div>
                          )}
                        </h3>
                        
                        {/* Price with Animation */}
                        <div className="flex items-center justify-center gap-3 text-accent-primary font-bold text-lg animate-pulse">
                          <Gem size={20} className="animate-spin-slow" />
                          <span className="drop-shadow-[0_0_10px_rgba(0,255,127,0.8)]">
                            {targetItem.price.toLocaleString()}
                          </span>
                          
                          {selectedMultiplier && (
                            <span className="px-2 py-1 rounded-md text-sm font-medium bg-accent-primary text-dark-primary">
                              x{selectedMultiplier}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Sparkle Effects */}
                    <div className="absolute -top-4 -left-4 w-4 h-4 bg-accent-primary rounded-full animate-ping"></div>
                    <div className="absolute -top-2 -right-6 w-2 h-2 bg-accent-secondary rounded-full animate-ping delay-300"></div>
                    <div className="absolute -bottom-4 -right-4 w-3 h-3 bg-accent-primary rounded-full animate-ping delay-700"></div>
                    <div className="absolute -bottom-2 -left-6 w-2 h-2 bg-accent-secondary rounded-full animate-ping delay-500"></div>
                  </div>
                ) : (
                  <div className="mb-6 flex justify-center">
                    <div className="w-32 h-32 rounded-full bg-red-500/10 border-4 border-red-500/30 flex items-center justify-center">
                      <XCircle size={64} className="text-red-400" />
                    </div>
                  </div>
                )}

                {/* Кнопки действий */}
                <div className="space-y-4 w-full max-w-sm mx-auto">
                  {/* DEBUG */}
                  {console.log('🔍 DEBUG Buttons:', { upgradeResult, isFirstSuccess, condition: upgradeResult && isFirstSuccess })}
                  
                  {/* Специальные кнопки для первого успешного апгрейда */}
                  {upgradeResult && isFirstSuccess ? (
                    <>
                      {/* Главная кнопка - Пригласи друга */}
                      <button
                        onClick={() => {
                          // Отправляем цель в Яндекс.Метрику
                          if (window.ym) {
                            window.ym(104258304, 'reachGoal', 'onboarding-invite-friend')
                          }

                          if (isWebBrowser) {
                            navigate('/referrals')
                            return
                          }

                          const { shareInviteMessage, user } = useAppStore.getState()
                          shareInviteMessage(
                            t('invite.message'),
                            user?.ref_link || '',
                            {
                              shareTitle: t('invite.shareTitle'),
                              copySuccess: t('invite.copySuccess')
                            }
                          )
                        }}
                        className="w-full h-14 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-all shadow-lg shadow-accent-primary/30"
                      >
                        <div className="flex items-center justify-center gap-3">
                          <Users size={22} />
                          <div className="flex flex-col items-start">
                            <span className="font-bold text-base">{t('upgrader.inviteFriendButton')}</span>
                            <span className="text-sm font-semibold flex items-center gap-1.5 mt-0.5">
                              {t('upgrader.inviteFriendReward')}
                              <Gem size={16} className="text-dark-primary" />
                            </span>
                          </div>
                        </div>
                      </button>

                      {/* Вторичная ссылка - Вывести в Steam */}
                      <div className="flex items-center justify-center pt-2 text-sm">
                        <button 
                          onClick={() => navigate('/inventory')}
                          className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors"
                        >
                          <ExternalLink size={16} />
                          <span className="whitespace-nowrap">{t('upgrader.withdrawToSteam')}</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Обычные кнопки для всех остальных случаев */}
                      <div className="space-y-3">
                        <button
                          onClick={handleReset}
                          className="w-full h-14 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-accent-primary/30"
                        >
                          <RefreshCw size={20} />
                          {t('upgrader.upgradeAgain')}
                        </button>
                        
                        {upgradeResult ? (
                          // При выигрыше - кнопка "В инвентарь"
                          <button
                            onClick={() => navigate('/inventory')}
                            className="w-full h-14 bg-surface border border-accent-primary/30 text-white font-semibold rounded-lg hover:border-accent-primary/50 hover:bg-surface/80 transition-all flex items-center justify-center gap-2"
                          >
                            {t('upgrader.ToInventory')}
                          </button>
                        ) : (
                          // При проигрыше - кнопка "На главную"
                          <button
                            onClick={() => navigate('/')}
                            className="w-full h-14 bg-surface border border-accent-primary/30 text-white font-semibold rounded-lg hover:border-accent-primary/50 hover:bg-surface/80 transition-all flex items-center justify-center gap-2"
                          >
                            {t('upgrader.backToHome')}
                          </button>
                        )}
                      </div>
                      
                      {/* Secondary Actions - Вторичные действия */}
                      {upgradeResult && (
                        <div className="flex items-center justify-center pt-2 text-sm">
                          <button 
                            onClick={() => navigate('/')}
                            className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors"
                          >
                            <Home size={16} />
                            <span className="whitespace-nowrap">{t('upgrader.backToHome')}</span>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={t('upgrader.helpTitle')}
        steps={[
          t('upgrader.helpStep1'),
          t('upgrader.helpStep2'),
          t('upgrader.helpStep3'),
          t('upgrader.helpStep4')
        ]}
        buttonText={t('upgrader.helpGotIt')}
      />

      {/* Seed Editor Modal */}
      <SeedEditor
        isOpen={isSeedEditorOpen}
        onClose={() => setIsSeedEditorOpen(false)}
      />
    </div>
  )
}

export default Upgrader
